import { z } from "zod";

import { simpleDateSchema } from "@/src/models/common-schema";
import { LabResultsDocumentType } from "@/src/models/constants/document-types";
import { FACILITY_TYPE_PROMPT, FacilityType } from "@/src/models/constants/facility-types";
import { LabSpecimenType } from "@/src/models/constants/lab-types";
import {
  IS_NEWBORN_PROMPT,
  LAB_NAME_PROMPT,
  ORDERING_PROVIDER_PROMPT,
  COLLECTION_DATE_PROMPT,
} from "@/src/models/constants/prompt";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import { locationSchema } from "@/src/models/location";
import { patientMinimalSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

export const labResultsTypeDisplay: Record<LabResultsDocumentType, string> = {
  [LabResultsDocumentType.A1C]: "A1C",
  [LabResultsDocumentType.BILIRUBIN]: "Bilirubin",
  [LabResultsDocumentType.BP]: "Blood Pressure",
  [LabResultsDocumentType.CBC]: "CBC",
  [LabResultsDocumentType.CMP]: "CMP",
  [LabResultsDocumentType.COLOGUARD]: "Cologuard",
  [LabResultsDocumentType.DRUG_SCREEN]: "Drug Screen",
  [LabResultsDocumentType.FIT]: "FIT",
  [LabResultsDocumentType.HEARING_TEST]: "Hearing Test",
  [LabResultsDocumentType.HEP]: "Hepatitis",
  [LabResultsDocumentType.LAB_RESULTS]: "Lab Results",
  [LabResultsDocumentType.LIPID_PANEL]: "Lipid Panel",
  [LabResultsDocumentType.MICROALBUMIN]: "Microalbumin",
  [LabResultsDocumentType.MULTIPLE_LABS]: "Multiple Labs",
  [LabResultsDocumentType.ORDER_FORM]: "Order Form/Lab Req",
  [LabResultsDocumentType.ORDER_FORM_NEEDS_SIGNATURE]: "Order Form Needs Signature",
  [LabResultsDocumentType.OTHER]: "Lab Results - Other",
  [LabResultsDocumentType.PAP_SMEAR]: "Pap Smear",
  [LabResultsDocumentType.PATHOLOGY]: "Pathology/Biopsy",
  [LabResultsDocumentType.PKU]: "PKU",
  [LabResultsDocumentType.PT_INR]: "PT-INR",
  [LabResultsDocumentType.QUANTAFLO]: "QuantaFlo",
  [LabResultsDocumentType.TB]: "TB",
  [LabResultsDocumentType.TEST_NOT_PERFORMED]: "Test Not Performed",
  [LabResultsDocumentType.URINALYSIS]: "Urinalysis",
};

export enum LabResultsValue {
  POSITIVE = "POSITIVE",
  NEGATIVE = "NEGATIVE",
  INDETERMINATE = "INDETERMINATE",
}

export const labResultsTypeSchema = z.nativeEnum(LabResultsDocumentType);

const baseLabResultsFields = {
  patient: patientMinimalSchema,
  dateOfLabResults: simpleDateSchema
    .nullable()
    .describe("Date the lab results were collected or reported. Format as YYYY-MM-DD."),
  collectionDate: simpleDateSchema.nullable().describe(COLLECTION_DATE_PROMPT),
  receivingLocation: locationSchema.nullable(),
};

const extendedLabResultsSchema = z.object({
  ...DocumentDateSchema.shape,
  orderingProvider: practitionerSchema.nullable().describe(ORDERING_PROVIDER_PROMPT),
  receivingProvider: practitionerSchema.nullable(),
  bodyPart: z.string().nullable(),
  needsSignature: z.boolean().nullable(),
  nameOfLab: z.string().nullable().describe(LAB_NAME_PROMPT),
  listOfNameOfLabs: z.array(z.string().describe(LAB_NAME_PROMPT)).nullable(),

  // Legacy single lab fields (for backward compatibility)
  typeOfLab: z.string().nullable(),
  labResult: z.nativeEnum(LabResultsValue).nullable(),
  result: z.nativeEnum(LabResultsValue).nullable(),
  documentType: z.string().nullable(),
  signatureRequired: z.boolean().nullable(),

  // New multiple labs field
  labTypesAndResults: z
    .array(
      z.object({
        typeOfLab: z.string().nullable(),
        labResult: z.nativeEnum(LabResultsValue).nullable(),
      })
    )
    .nullable(),
  location: locationSchema.nullable(),
  facilityType: z.nativeEnum(FacilityType).nullable().describe(FACILITY_TYPE_PROMPT),
  testNotPerformed: z
    .boolean()
    .nullable()
    .describe("True when a lab specimen was collected but the test wasn't performed"),
  specimenCollected: z
    .boolean()
    .nullable()
    .describe("True when the document indicates a specimen was actually collected from the patient"),
  specimenType: z.nativeEnum(LabSpecimenType).nullable(),
  resultsPresent: z.boolean().nullable().describe("True when lab results are present in this document"),
  isOrderedBySpecialist: z.boolean().nullable(),
  isNewborn: z.boolean().nullable().describe(IS_NEWBORN_PROMPT),
  companyName: z
    .string()
    .nullable()
    .describe("The name of the laboratory or facility that performed or sent these lab results."),
  significantFindings: z.boolean().nullable(),
  isLabAddOn: z.boolean().nullable(),
});

const labResultsCustomFields: Record<LabResultsDocumentType, Record<string, z.ZodTypeAny>> = {
  [LabResultsDocumentType.LAB_RESULTS]: extendedLabResultsSchema.shape,
  [LabResultsDocumentType.A1C]: extendedLabResultsSchema.pick({ dateOfEvent: true, orderingProvider: true }).shape,
  [LabResultsDocumentType.CBC]: extendedLabResultsSchema.pick({ dateOfEvent: true, orderingProvider: true }).shape,
  [LabResultsDocumentType.CMP]: extendedLabResultsSchema.pick({ dateOfEvent: true, orderingProvider: true }).shape,
  [LabResultsDocumentType.MULTIPLE_LABS]: extendedLabResultsSchema.pick({
    dateOfEvent: true,
    nameOfLab: true,
    listOfNameOfLabs: true,
    orderingProvider: true,
    significantFindings: true,
  }).shape,
  [LabResultsDocumentType.COLOGUARD]: extendedLabResultsSchema.pick({ dateOfEvent: true, orderingProvider: true })
    .shape,
  [LabResultsDocumentType.DRUG_SCREEN]: extendedLabResultsSchema.pick({ dateOfEvent: true, orderingProvider: true })
    .shape,
  [LabResultsDocumentType.FIT]: extendedLabResultsSchema.pick({ dateOfEvent: true, orderingProvider: true }).shape,
  [LabResultsDocumentType.LIPID_PANEL]: extendedLabResultsSchema.pick({ dateOfEvent: true, orderingProvider: true })
    .shape,
  [LabResultsDocumentType.MICROALBUMIN]: extendedLabResultsSchema.pick({ dateOfEvent: true, orderingProvider: true })
    .shape,
  [LabResultsDocumentType.ORDER_FORM]: extendedLabResultsSchema.pick({
    dateOfEvent: true,
    isLabAddOn: true,
    needsSignature: true,
    orderingProvider: true,
  }).shape,
  [LabResultsDocumentType.OTHER]: extendedLabResultsSchema.pick({
    dateOfEvent: true,
    nameOfLab: true,
    listOfNameOfLabs: true,
    orderingProvider: true,
    receivingProvider: true,
    facilityType: true,
    testNotPerformed: true,
    specimenCollected: true,
    resultsPresent: true,
    isOrderedBySpecialist: true,
    isNewborn: true,
  }).shape,
  [LabResultsDocumentType.PAP_SMEAR]: extendedLabResultsSchema.pick({ dateOfEvent: true, orderingProvider: true })
    .shape,
  [LabResultsDocumentType.PATHOLOGY]: extendedLabResultsSchema.pick({
    dateOfEvent: true,
    bodyPart: true,
    nameOfLab: true,
    listOfNameOfLabs: true,
    orderingProvider: true,
    receivingProvider: true,
    labTypesAndResults: true,
    documentType: true,
    signatureRequired: true,
  }).shape,
  [LabResultsDocumentType.PT_INR]: extendedLabResultsSchema.pick({ dateOfEvent: true, orderingProvider: true }).shape,
  [LabResultsDocumentType.TB]: extendedLabResultsSchema.shape,
  [LabResultsDocumentType.TEST_NOT_PERFORMED]: extendedLabResultsSchema.pick({ dateReceived: true }).shape,
  [LabResultsDocumentType.ORDER_FORM_NEEDS_SIGNATURE]: extendedLabResultsSchema.pick({
    dateReceived: true,
    receivingProvider: true,
  }).shape,
  [LabResultsDocumentType.HEP]: extendedLabResultsSchema.shape,
  [LabResultsDocumentType.QUANTAFLO]: extendedLabResultsSchema.shape,
  [LabResultsDocumentType.BP]: extendedLabResultsSchema.shape,
  [LabResultsDocumentType.BILIRUBIN]: extendedLabResultsSchema.shape,
  [LabResultsDocumentType.HEARING_TEST]: extendedLabResultsSchema.shape,
  [LabResultsDocumentType.PKU]: extendedLabResultsSchema.shape,
  [LabResultsDocumentType.URINALYSIS]: extendedLabResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
    companyName: true,
  }).shape,
};

const makeLabResultsSchema = (type: LabResultsDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...baseLabResultsFields,
    ...customFields,
  });

export const labResultsSchemas: Record<LabResultsDocumentType, z.ZodObject<any>> = (
  Object.values(LabResultsDocumentType) as LabResultsDocumentType[]
).reduce(
  (acc, type) => {
    acc[type] = makeLabResultsSchema(type, labResultsCustomFields[type]);

    return acc;
  },
  {} as Record<LabResultsDocumentType, z.ZodObject<any>>
);

const labResultsSchemasArray = (Object.values(LabResultsDocumentType) as LabResultsDocumentType[]).map(
  (type) => labResultsSchemas[type]
) as [z.ZodObject<any>, ...z.ZodObject<any>[]];

export const anyLabResultsSchema = z.discriminatedUnion("type", labResultsSchemasArray);

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
export type LabResults = ExtractedDocumentFields<
  LabResultsDocumentType,
  typeof extendedLabResultsSchema,
  typeof baseLabResultsFields
> &
  DatabaseDefaults;

export const labResultsShape: LabResults = {
  id: "",
  patient: null,
  receivingLocation: null,
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  dateOfLabResults: null,
  groupId: "",
  collectionDate: null,
  type: LabResultsDocumentType.OTHER,
  receivingProvider: null,
  needsSignature: null,
  bodyPart: null,
  dateOfDocument: null,
  dateReceived: null,
  dateSigned: null,
  dateOfEvent: null,
};
